import store from '@state/store'
import con from '@services/connection'
import { APIChat } from '@modules/aawz-intelligence-v2/api/apiChat'
import { AGENTS_DISPONIVEIS, AGENT_CHAT } from '@modules/ai-framework/constants/URL'
import { CHAVE_ACAO_PROPOSTA } from '@modules/ai-framework/ia/registry'

/**
 * Cliente do chat do agente (Central de IA) — spec-001 do
 * `project-plataforma-por-chat`, [T018].
 *
 * ★ ESTA CLASSE NÃO TEM PARSER. Até esta task o módulo mantinha um segundo
 * parser SSE (drain do `ReadableStream`, montagem de frame, `JSON.parse`) ao
 * lado do do IA CRM. Dois parsers significam dois lugares para corrigir a
 * mesma perda de evento e duas máquinas de estado divergindo em silêncio — o
 * turno do parceiro tinha um comportamento e o do gestor, outro. Agora existe
 * UM: `APIChat` (`@modules/aawz-intelligence-v2/api/apiChat`). O que sobra
 * aqui é o que de fato é DESTE canal:
 *
 * | o que | por que é próprio |
 * |---|---|
 * | rota por instância (`/agents/<id>/chat`) | cada agente ativado é uma instância |
 * | corpo `{query, id_conversa}` | o `reqparse` do agent-chat só registra esses dois |
 * | `id_run` do `start`/`end` | trilha de auditoria do run (spec-004) |
 * | `acoes_pendentes` do `end` | a escrita NUNCA executa no turno (RF6.1) |
 * | recusa de gate visível | o agente pode estar pausado/órfão/no teto |
 * | sem reconnect | o backend não publica a rota |
 *
 * ## O `gate_error` deixou de existir
 *
 * Antes, a falha de gate ANTES do stream (envelope JSON em HTTP-200-always)
 * viajava num pseudo-evento LOCAL chamado `gate_error`, que só esta superfície
 * conhecia. Ele era, o tempo todo, um ERRO — e agora entra pelo caminho de erro
 * do motor (`_tratarRespostaNaoStream` → `_onEventError`), com o mesmo envelope
 * das outras três superfícies. O que a superfície ainda decide é o DESTINO: a
 * recusa do gate mostra o texto EXATO do backend e recarrega o seletor de
 * agentes (RF4.1e/RF4.2); a falha no meio do stream preserva o parcial e mostra
 * a copy genérica.
 *
 * Contrato: `contracts/events.json` · `contracts/ui-components.md` ·
 * `aawz_ia_framework/usecases/usecase_agent_run.py` (quem emite os eventos).
 */

const STORE = 'aiFramework'

const ERRO_INESPERADO = 'Erro inesperado. Tente novamente mais tarde.'
const SEM_AGENTE_SELECIONADO =
  'Selecione um agente antes de enviar a mensagem. Se a lista estiver vazia, recarregue a página.'
const SEM_RECONEXAO =
  'A conversa foi interrompida. Envie a pergunta de novo; se persistir, contate o suporte.'

/**
 * O agent-chat publica UMA rota (`aawz_ia_framework/routes.py:69`):
 * `POST /agents/<id_instance>/chat`. Não existem `status`, `reconnect` nem
 * `cancel` — e por isso os três entram explicitamente como `null`, no lugar de
 * herdarem os do IA CRM. Herdados, um erro com `id` faria o motor reconectar
 * na rota do CRM com o id de uma conversa do agente.
 *
 * `chat` também é `null`: a rota depende da INSTÂNCIA, e é montada no envio
 * (`_endpointDoChat`).
 */
const ENDPOINTS_DO_AGENTE = {
  chat: null,
  chatStatus: null,
  chatReconnect: null,
  chatCancel: null,
}

// Estado da parte de tool que carrega a proposta. Ela nasce JÁ resolvida: no
// ia_framework a write não executa — a proposta É o desfecho da tool.
const ESTADO_DA_PROPOSTA = 'output-available'

// O fio do agent-chat não traz `toolCallId` (o vocabulário tipado do [T002] é
// do `aawz_intelligence_v2`; aqui só existe o antigo). O id é sintetizado por
// POSIÇÃO no turno, o que dá duas coisas de uma vez: identidade estável para o
// `upsertToolPart` e o pareamento com os `acoes_pendentes` do `end`, que o
// backend produz na mesma ordem (`usecase_agent_run.py:270,318`).
const PREFIXO_DA_PROPOSTA = 'acao-proposta-'

function idDaProposta(indice) {
  return PREFIXO_DA_PROPOSTA + (indice + 1)
}

class APIAgentChat extends APIChat {
  constructor() {
    super({ storeNamespace: STORE, endpoints: ENDPOINTS_DO_AGENTE })
    // Instância do agente do turno. Estado de INSTÂNCIA e não de módulo, pela
    // mesma razão do `abortController` do motor: dois consumidores simultâneos
    // mandariam a pergunta de um para o agente do outro.
    this._idInstance = null
  }

  /**
   * Os buffers do turno. `_propostasDoTurno` é o que a mensagem persistida
   * leva em `tool_calls[]` — sem ele o cartão sumiria da thread no instante em
   * que o turno fecha (a bolha troca as partes do streaming pelas da mensagem).
   */
  _resetarEstadoDaInstancia() {
    super._resetarEstadoDaInstancia()
    this._propostasDoTurno = []
    // O envelope da recusa de gate, preenchido só enquanto a resposta sem
    // stream está sendo tratada — ver `_tratarRespostaNaoStream`/`_onEventError`.
    this._envelopeDaRecusa = null
  }

  // ---------------------------------------------------------------------
  // Rota e corpo
  // ---------------------------------------------------------------------

  /**
   * A rota é POR INSTÂNCIA, então é montada no envio — e não fixada no
   * construtor como nas superfícies do IA CRM, onde o endpoint é constante.
   */
  _endpointDoChat() {
    return AGENT_CHAT(this._idInstance)
  }

  /**
   * O corpo do agent-chat: `query` + `id_conversa` + a tela aberta (US-5).
   *
   * ★ `id_conversa`, e NÃO `conversation_id`: é o argumento que o
   * `resource_agent_chat.py:62` registra. O `reqparse` descarta o que não está
   * registrado — herdar o corpo do IA CRM abriria uma conversa nova a cada
   * mensagem, em silêncio, com o histórico se perdendo pelo caminho.
   *
   * Também não viajam `model`, `persona`, `answer_style` nem
   * `thinking_enabled`: o parceiro não escolhe modelo nem persona (NC-3/RF2.6),
   * quem os fixa é a instância do agente no backend. Eles seriam descartados de
   * qualquer forma — mas o corpo é contrato, e o que não é deste canal não sai.
   */
  _buildChatBody({ message, conversationId } = {}) {
    const corpo = { query: message, id_conversa: conversationId || null }
    Object.assign(corpo, this._contextoDeTela())
    return corpo
  }

  /**
   * Abre o turno com o agente da instância.
   *
   * @param {Object}  opts
   * @param {number}  opts.idInstance      Instância do agente (tenant-locked no backend).
   * @param {string}  opts.message         Pergunta do usuário.
   * @param {string=} opts.conversationId  Conversa a continuar (ausente = nova).
   */
  sendMessage({ idInstance, message, conversationId } = {}) {
    if (!idInstance) {
      // Falha ALTA: sem instância a URL viraria `/agents/null/chat` e o erro
      // apareceria como 404 do servidor, longe da causa. `Promise.resolve` para
      // a recusa ter o MESMO tipo de retorno do caminho feliz — quem chama dá
      // `await` nos dois.
      return Promise.resolve(
        this.buildErrorResponse({ message: SEM_AGENTE_SELECIONADO, status: 400 })
      )
    }
    this._idInstance = idInstance
    return super.sendMessage({ message: message, conversationId: conversationId })
  }

  /**
   * Cancela o stream em curso (o backend fecha o run `client_disconnected`).
   *
   * Nome preservado porque é o que a store e a view chamam. Não usa o
   * `cancelRequest` do motor: aquele fala com `POST /chat/cancel`, rota que
   * este canal não tem.
   */
  abort() {
    this._abortarStreamAtivo()
    this._clearTokenBuffer()
  }

  /**
   * Fonte do seletor de agentes utilizáveis (RF7.2). HTTP-200-always: o caller
   * lê `r.status` do corpo (200 lista | 403 flag OFF | 500 erro).
   */
  async getAgentsDisponiveis() {
    const r = await con.get(AGENTS_DISPONIVEIS)
    return r || { status: 500, message: ERRO_INESPERADO, value: null }
  }

  /**
   * Não há rota de reconexão neste canal (`routes.py:69` publica só o POST do
   * chat). O motor chama `reconnect` sozinho quando o evento `error` traz `id`
   * — e o `error` do agent-chat traz o id da CONVERSA
   * (`usecase_agent_run.py:137`). Sem este fecho, a queda de um turno do
   * parceiro dispararia um GET na rota do IA CRM.
   */
  reconnect() {
    store.dispatch(STORE + '/setChatLoading', false)
    // Sem `async`: não há ida à rede aqui, e devolver o envelope direto é o que
    // deixa o fecho legível. O `await` de quem chama continua valendo.
    return this.buildErrorResponse({ message: SEM_RECONEXAO })
  }

  // ---------------------------------------------------------------------
  // Eventos que só este canal emite
  // ---------------------------------------------------------------------

  /**
   * Este canal NÃO acompanha run pelo motor.
   *
   * O `id_run` do `start` é trilha de auditoria (spec-004), não cursor de
   * relay: as rotas de acompanhamento (`GET /runs/<id>/eventos`,
   * `GET /conversas/<id>/run-atual`) são do `aawz_intelligence_v2`, e o
   * agent-chat publica só o `POST /agents/<id>/chat`. Com o acompanhamento
   * armado, um `error` no meio do turno do parceiro disparava um SEGUNDO
   * request — `GET /api/v1/intelligence-v2/runs/<id_run>/eventos?desde=N` —
   * com o id de um run que aquele módulo não conhece. É o mesmo fecho que
   * `reconnect()` abaixo faz, uma camada acima: aqui o acompanhamento nem
   * nasce.
   */
  _acompanhaRunPeloMotor() {
    return false
  }

  /**
   * `start` traz `id_run` além do `conversation_id`: é a chave da trilha de
   * auditoria do run (nunca exibida crua — só correlaciona a fila do gestor).
   */
  _onEventStart(data) {
    super._onEventStart(data)
    store.dispatch(STORE + '/setRunIdDoTurno', (data && data.id_run) || null)
  }

  /**
   * A proposta de escrita — o `tool_result` com `requer_confirmacao`.
   *
   * O `super` alimenta a lista de propostas do run; o que se acrescenta aqui é
   * a PARTE de tool que desenha o cartão na conversa, com o `ui.component`
   * explícito. O agent-chat ainda não emite `ui` no fio, e o nome da tool não
   * serve de chave (o agente chama as mesmas write-tools do IA CRM) — quem sabe
   * que toda proposta desta superfície é uma "ação proposta" é a superfície.
   * A razão longa está no cabeçalho de `ia/registry.js`.
   */
  _maybeCapturePendingConfirmation(data) {
    super._maybeCapturePendingConfirmation(data)
    if (!data) return
    const value = data.value || {}
    if (!data.requer_confirmacao && !value.requer_confirmacao) return
    this._registrarProposta({ tool: data.tool || '', value: value })
  }

  _registrarProposta(proposta) {
    const indice = this._propostasDoTurno.length
    this._propostasDoTurno.push(proposta)
    store.dispatch(STORE + '/upsertToolPart', this._parteDaProposta(indice, proposta))
  }

  _parteDaProposta(indice, proposta) {
    return {
      toolCallId: idDaProposta(indice),
      tool: proposta.tool,
      state: ESTADO_DA_PROPOSTA,
      value: proposta.value,
      ui: { component: CHAVE_ACAO_PROPOSTA, props: proposta.value },
    }
  }

  /**
   * As partes que a mensagem PERSISTIDA carrega.
   *
   * O motor monta aqui os cartões do IA CRM (serviços citados, painel); nesta
   * superfície o que sobrevive ao fim do turno são as propostas. Sem isto o
   * cartão apareceria durante o streaming e sumiria no `end`: a bolha troca as
   * partes do slice `toolParts` pelas de `message.tool_calls[]` no instante em
   * que a mensagem é materializada (`chat-message.vue`).
   */
  _buildToolCallsRegistrados() {
    return this._propostasDoTurno.map(function(proposta, indice) {
      return {
        name: proposta.tool,
        input: {},
        id: idDaProposta(indice),
        output: proposta.value,
        ui: { component: CHAVE_ACAO_PROPOSTA, props: proposta.value },
      }
    })
  }

  /**
   * O fechamento do turno.
   *
   * `acoes_pendentes` são os resumos humanos CANÔNICOS (NC-8) e prevalecem
   * sobre o que os `tool_result` acumularam — é o backend consolidando o que
   * ficou pendente no run. O pareamento é POSICIONAL porque as duas listas
   * nascem da mesma (`totals.proposals`); uma proposta que só apareça no `end`
   * (o `tool_result` se perdeu) vira cartão mesmo assim, em vez de sumir.
   */
  _onEventEnd(data) {
    const valor = (data && data.value) || {}
    this._absorverAcoesCanonicas(valor.acoes_pendentes)
    super._onEventEnd(data)
    if (valor.id_run) store.dispatch(STORE + '/setRunIdDoTurno', valor.id_run)
    if (this._propostasDoTurno.length) {
      store.dispatch(
        STORE + '/setAcoesPendentes',
        this._propostasDoTurno.map(function(proposta) { return proposta.value })
      )
    }
    // O backend pode ter criado ou renomeado a conversa neste turno.
    store.dispatch(STORE + '/fetchChatConversations')
  }

  _absorverAcoesCanonicas(acoes) {
    if (!acoes || !acoes.length) return
    const capturadas = this._propostasDoTurno
    this._propostasDoTurno = acoes.map(function(canonica, indice) {
      const capturada = capturadas[indice] || { tool: '', value: {} }
      return { tool: capturada.tool, value: { ...capturada.value, ...canonica } }
    })
    // Proposta capturada que o `end` não confirmou continua na thread: o cartão
    // já está na tela, e apagá-lo deixaria o gestor sem saber o que o agente
    // propôs.
    this._propostasDoTurno = this._propostasDoTurno.concat(capturadas.slice(acoes.length))
  }

  /**
   * O erro do turno vai à TELA — nas duas caras que ele tem aqui.
   *
   * A discriminação é EXPLÍCITA (`_erroDeEnvelope`), e não pela forma do
   * payload: os dois chegam como `{status?, message}` e adivinhar pela ausência
   * de uma chave é o tipo de regra que envelhece sem nada ficar vermelho.
   *
   * O flush ANTES do `super` existe porque o motor esvazia o buffer de tokens
   * dentro dele: sem o flush, o último quadro de texto que o modelo escreveu se
   * perderia justamente na hora em que a resposta parcial é o que sobra.
   */
  _onEventError(data) {
    this._flushTokenBuffer()
    const envelope = this._envelopeDaRecusa
    super._onEventError(data)
    if (envelope) {
      // O envelope INTEIRO, e não o `data` que o motor repassa: aquele é
      // reduzido a `{status, message}`, e o 429 do teto diário carrega
      // `value.retry_after_seconds` (`helpers_channel.gate_envelope`).
      store.dispatch(STORE + '/registrarRecusaDoGate', envelope)
      return
    }
    store.dispatch(STORE + '/registrarErroDoStream', data)
  }

  /**
   * Resposta que não é stream — o envelope JSON em HTTP-200-always, que aqui é
   * sempre a recusa de um gate (agente pausado, sem permissão, teto diário).
   * O motor já a transforma em erro; a marca serve para o `_onEventError`
   * saber QUAL das duas caras está tratando.
   */
  _tratarRespostaNaoStream(response) {
    this._envelopeDaRecusa = response || null
    try {
      return super._tratarRespostaNaoStream(response)
    } finally {
      this._envelopeDaRecusa = null
    }
  }
}

const apiAgentChat = new APIAgentChat()

export { apiAgentChat, APIAgentChat }
